import camelcaseKeysDeep from "camelcase-keys-deep";
import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Button, Label } from "semantic-ui-react";
import { ContactInfoOptions } from "../../permits/types/ContactInfoOptions";
import { isObjectEmpty } from "../../../utils/functions";
import { ContactFieldsV2 } from "./ContactFieldsV2";
import "./ControlledContactInfo.scss";

interface IControlledContactInfo {
  fieldType?: "additional_contacts" | "contact_info";
  name: string;
  control: any;
  isApplicant?: boolean;
  contactRole: string;
  readonly: boolean;
  required: boolean;
  contactInfoOptions: ContactInfoOptions;
  formFieldId?: string;
  isApplyingScreen?: boolean;
  defaultValues?: any;
  displayInputFields?: boolean;
  allowInviteUser?: boolean;
  showDivider?: boolean;
  resetTriggered?: boolean;
  readOnlyValuesTriggered?: boolean;
  applicantInfoFieldIndex?: number;
  autofillCountry?: boolean;
}

export const ControlledContactInfo = (props: IControlledContactInfo) => {
  const {
    fieldType,
    name,
    isApplicant,
    contactRole,
    formFieldId,
    readonly,
    required,
    contactInfoOptions,
    resetTriggered = false,
    readOnlyValuesTriggered = false,
    defaultValues,
    isApplyingScreen = false,
    displayInputFields: displayInputFieldsProp = false,
    allowInviteUser = true,
    applicantInfoFieldIndex,
    autofillCountry = false,
  } = props;

  const [displayInputFields, setDisplayInputFields] = useState<boolean>(
    !isObjectEmpty(defaultValues) || displayInputFieldsProp,
  );

  useEffect(() => {
    setDisplayInputFields(!isObjectEmpty(defaultValues) || displayInputFieldsProp);
  }, [defaultValues, displayInputFieldsProp]);

  const formContext = useFormContext();
  const { errors, reset, getValues, watch, setValue, clearErrors } = formContext;
  const isErrorSelector = (errors: any) => (fieldName: string) => !!_.get(errors, `${fieldName}.message`);
  const isError = isErrorSelector(errors)("addContact");
  const resetApplicantFormSection = () => {
    setValue("permits_extensions_attributes.0.value.firstName", "");
    setValue("permits_extensions_attributes.0.value.lastName", "");
    setValue("permits_extensions_attributes.0.value.email", "");
    setValue("permits_extensions_attributes.0.value.phone", "");
    setValue("permits_extensions_attributes.0.value.licenseNumber", "");
    setValue("permits_extensions_attributes.0.value.companyName", "");
    setValue("permits_extensions_attributes.0.value.address", "");
    setValue("permits_extensions_attributes.0.value.address2", "");
    setValue("permits_extensions_attributes.0.value.city", "");
    setValue("permits_extensions_attributes.0.value.state", "");
    setValue("permits_extensions_attributes.0.value.zip", "");
  };
  const setReadOnlyValues = () => {
    setValue("permits_extensions_attributes.0.value.firstName", defaultValues?.firstName);
    setValue("permits_extensions_attributes.0.value.lastName", defaultValues?.lastName);
    setValue("permits_extensions_attributes.0.value.email", defaultValues?.email);
  };

  useEffect(() => {
    if (resetTriggered) {
      // When the "I am not the applicant" radio is checked, clear the fields on the applicant section.
      resetApplicantFormSection();
    }
    if (readOnlyValuesTriggered) {
      // When the "I am the applicant" radio is checked, set the read only fields to their default values.
      setReadOnlyValues();
    }
  }, [resetTriggered, readOnlyValuesTriggered]);

  const additionalContacts = watch(`${name}.contacts`);
  const hasAdditionalContact =
    additionalContacts &&
    additionalContacts.some((contact: any) => {
      return !!contact.visibility;
    });

  // Get entered values for contact info fields
  const keys = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "licenseNumber",
    "companyName",
    "address",
    "address2",
    "city",
    "state",
    "zip",
    "physical_address",
    "physical_address2",
    "physical_city",
    "physical_state",
    "physical_zip",
    "sameMailAndPhysicalAddress",
  ];
  const enteredValues: { [key: string]: any } = {};
  for (const key of keys) {
    const variableName = `entered${key}`;
    enteredValues[variableName] = watch(`permits_extensions_attributes.${applicantInfoFieldIndex}.value.${key}`);
  }

  // Remove an additional contact field
  const removeAdditionalContact = (visibilityField: string) => {
    setValue(visibilityField, false); // Sets the visibility of a contact to false, preventing it from showing
  };

  const contactButtonRef = useRef<any>(null);
  const additionalContactButtonRef = useRef<any>(null);

  // Remove a contact info field
  const removeContact = () => {
    const permits_extensions_attributes = getValues()?.permits_extensions_attributes;
    const field = permits_extensions_attributes.find((field: any) => field?.permits_form_field_id === formFieldId);
    field.value = "";

    // Resets the form state, replaces permits_extensions_attributes with the new values
    reset({
      ...getValues(),
      permits_extensions_attributes,
    });
    setDisplayInputFields(false);
  };

  // If read-only or this is the view permit screen, display nothing (TODO: Change this to the contact card)
  if (readonly || !isApplyingScreen) return <span></span>;

  // Ref to set focus for the first field in the contact info form after adding a new contact. This is for WCAG.
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {/* Display Additional Contacts field */}
      {!readonly && fieldType === "additional_contacts" && (
        <div>
          {/* Render multiple contact info fields */}
          {additionalContacts?.map((contact: any, index: any) => {
            // If contact is deleted, don't render it
            if (!contact.visibility) return;

            return (
              <div key={index}>
                {/* Contact info fields */}
                <ContactFieldsV2
                  autofillCountry={autofillCountry}
                  showContactTypeField={true}
                  showMailingAddressHeader={false}
                  physicalAddressPrefix={"physical"}
                  showInviteUser={allowInviteUser}
                  namePrefix={`${name}.contacts.${index}`}
                  defaultContactFields={camelcaseKeysDeep(contact)}
                  visibilityOptions={contactInfoOptions}
                  inputRef={inputRef}
                  deleteButton={
                    <Button
                      className='no-padding-button'
                      onClick={() => {
                        removeAdditionalContact(`${name}.contacts.${index}.visibility`);
                        additionalContactButtonRef.current.focus();
                      }}
                      icon='trash alternate outline'
                      content={`Remove ${contactRole}`}
                      size='small'
                      type='button'
                    />
                  }
                />
              </div>
            );
          })}

          {/* Add Contact Button */}
          <Button
            secondary
            className='citizen-add-contact-button'
            size='small'
            ref={additionalContactButtonRef}
            onClick={(e) => {
              e.preventDefault();
              const newContactFields = { "visibility": true, "contact-id": null, "isApplicant": false };
              // if there are already additional contacts, add a new one to the end of the array
              if (additionalContacts) setValue(`${name}.contacts`, [...additionalContacts, newContactFields]);
              // if there are no additional contacts, create a new array with one contact
              else setValue(`${name}.contacts`, [newContactFields]);
              //Set focus from input ref after a brief delay, so it has time to render
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }, 100);
              clearErrors("addContact");
            }}
          >
            Add {contactRole}
          </Button>

          {/* Error message */}
          {!hasAdditionalContact && (
            <>
              <div>
                {isError && required && (
                  <Label basic size='large' prompt pointing='above'>
                    {contactRole} is required
                  </Label>
                )}
              </div>

              {/* Prevents going to next section if contact has not been added */}
              {required && (
                <Controller
                  required={required}
                  rules={{
                    validate: { validateContactButton: () => false || "This field is required" },
                    required: required && "This field is required",
                  }}
                  as={<input type='hidden' />}
                  name={`addContact`}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Display Contact Information field */}
      {!readonly && fieldType === "contact_info" && (
        <div>
          {/* Add Contact Button */}
          {!displayInputFields && (
            <>
              {/* Button */}

              <Button
                secondary
                size='small'
                className='tw-mb-2'
                ref={contactButtonRef}
                onClick={() => {
                  setDisplayInputFields(true);
                  //Set focus from input ref after a brief delay
                  setTimeout(() => {
                    if (inputRef.current) {
                      inputRef.current.focus();
                    }
                  }, 100);
                  clearErrors("addContact");
                }}
              >
                Add {contactRole}
              </Button>

              {/* Error message */}
              <div>
                {isError && required && (
                  <Label basic size='large' prompt pointing='above'>
                    {contactRole} is required
                  </Label>
                )}
              </div>

              {/* Prevents going to next section if contact has not been added */}
              {required && (
                <Controller
                  required={required}
                  rules={{
                    validate: { validateContactButton: () => false || "This field is required" },
                    required: required && "This field is required",
                  }}
                  as={<input type='hidden' />}
                  name={`addContact`}
                />
              )}
            </>
          )}

          {/* Contact Input Fields */}
          {displayInputFields && (
            <ContactFieldsV2
              autofillCountry={autofillCountry}
              showContactTypeField={false}
              contactTypeValue={contactRole}
              showMailingAddressHeader={false}
              showInviteUser={allowInviteUser}
              namePrefix={name}
              physicalAddressPrefix={"physical"}
              showLicenseAndCompanyFields={true}
              defaultContactFields={camelcaseKeysDeep(defaultValues)}
              visibilityOptions={contactInfoOptions}
              isSubmitterSection={contactRole === "Applicant"}
              isApplicant={isApplicant}
              enteredValues={enteredValues}
              inputRef={inputRef}
              deleteButton={
                <Button
                  className='no-padding-button'
                  type='button'
                  onClick={() => {
                    removeContact();
                    setTimeout(() => {
                      if (contactButtonRef.current) {
                        contactButtonRef.current.focus();
                      }
                    }, 100);
                  }}
                  icon='trash alternate outline'
                  content={`Remove ${contactRole}`}
                  size='small'
                />
              }
            />
          )}
        </div>
      )}
    </>
  );
};
